import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TuneIcon from '@mui/icons-material/Tune';

import {
  deleteMapping,
  fetchIntegrationHealth,
  processManualUpload,
  updateMapping,
} from '@src/api/actions/dashboard/dataIntegrations';
import { useMessage } from '@src/context/common/MessageContext';
import paths from '@src/paths';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DataIntegrationEditDialog from './DataIntegrationEditDialog';
import DataIntegrationRemapDialog from './DataIntegrationRemapDialog';

const TYPE_LABELS = {
  email: 'Automated Email',
  sftp: 'SFTP',
  api: 'API',
  google_sheet: 'Google Sheet',
  manual: 'Manual Upload',
};

const STATUS_COLORS = {
  succeeded: 'success',
  success: 'success',
  failed: 'error',
  running: 'info',
};

function timeSince(isoString) {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function DataIntegrationHealthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-row busy state for the toggle / delete / upload actions so we can
  // show a spinner on just the row that's in flight.
  const [rowBusy, setRowBusy] = useState({});

  // Edit / Remap / delete confirm dialogs
  const [editing, setEditing] = useState(null);
  const [remapping, setRemapping] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Column-drift recovery state: when a manual upload 409's because the
  // file's columns don't match the saved mapping, we stash the File + the
  // diff so we can open the Remap dialog with the file pre-loaded and
  // auto-retry the upload after the user saves a fixed mapping.
  const [driftSample, setDriftSample] = useState(null);
  const [driftInfo, setDriftInfo] = useState(null);

  // Use the app-wide notistack provider rather than a local MUI Snackbar —
  // the default Snackbar content crashes on the app's theme due to a MUI
  // `emphasize(var(--mui-palette-common-white))` incompatibility.
  const message = useMessage();

  // Hidden file input for the manual re-upload flow; we reuse one element
  // and point it at whatever row triggered the click.
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (clientId) params.filter_client = clientId;
      const res = await fetchIntegrationHealth(params);
      setIntegrations(res.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load integration health.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const rowKey = (item) => `${item.integration_type}-${item.id}`;
  const setBusy = (key, v) => setRowBusy((prev) => ({ ...prev, [key]: v }));

  const handleToggleEnabled = async (item, nextEnabled) => {
    const key = rowKey(item);
    setBusy(key, true);
    // Optimistic UI — flip first, roll back on failure.
    setIntegrations((prev) => prev.map((r) => (
      rowKey(r) === key ? { ...r, enabled: nextEnabled } : r
    )));
    try {
      await updateMapping(item.integration_type, item.id, { enabled: nextEnabled });
      message.success(nextEnabled ? 'Integration resumed' : 'Integration paused');
    } catch (err) {
      // Roll back the optimistic update.
      setIntegrations((prev) => prev.map((r) => (
        rowKey(r) === key ? { ...r, enabled: !nextEnabled } : r
      )));
      message.error(err?.response?.data?.detail || err?.message || 'Failed to update.');
    } finally {
      setBusy(key, false);
    }
  };

  const handleDelete = async (item) => {
    const key = rowKey(item);
    setBusy(key, true);
    try {
      await deleteMapping(item.integration_type, item.id);
      setIntegrations((prev) => prev.filter((r) => rowKey(r) !== key));
      message.success('Integration removed');
    } catch (err) {
      message.error(err?.response?.data?.detail || err?.message || 'Failed to delete.');
    } finally {
      setBusy(key, false);
      setConfirmDelete(null);
    }
  };

  const handleUploadClick = (item) => {
    setUploadingFor(item);
    // Reset so selecting the same file twice still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  // Shared upload runner — used by the file-picker flow AND by the
  // auto-retry after a drift-triggered remap. `force` bypasses the server's
  // column-drift 409 guard (we only set it for the post-remap retry).
  const runManualUpload = async (item, file, { force = false } = {}) => {
    const key = rowKey(item);
    setBusy(key, true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('client', item.client_id);
      form.append('mapping_id', item.id);
      const res = await processManualUpload(form, { force });
      const processed = res.data?.processed_count ?? 0;
      const errorCount = res.data?.error_count ?? 0;
      if (processed > 0 && errorCount === 0) {
        message.success(`Imported ${processed} rows from ${file.name}`);
      } else if (processed > 0 && errorCount > 0) {
        message.error(
          `Imported ${processed} rows from ${file.name}, but ${errorCount} failed. `
          + 'Check the Import History for details.'
        );
      } else {
        message.error(
          `Import from ${file.name} failed: 0 rows imported, ${errorCount} errors. `
          + 'The file may not match the saved column mapping — click Remap Columns to fix.'
        );
      }
      await loadHealth();
      return true;
    } catch (err) {
      // Column-drift 409: open the Remap dialog pre-loaded with the file.
      const resp = err?.response;
      if (resp?.status === 409 && resp?.data?.columns_changed) {
        setDriftSample(file);
        setDriftInfo(resp.data);
        setRemapping(item);
        message.error(
          resp.data?.detail
          || 'The file\'s columns don\'t match the saved mapping. Remap below to continue.'
        );
        return false;
      }
      message.error(resp?.data?.detail || err?.message || 'Upload failed.');
      return false;
    } finally {
      setBusy(key, false);
    }
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFor) return;
    const item = uploadingFor;
    setUploadingFor(null);
    await runManualUpload(item, file);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Data Integrations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage your backend data connections.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate(paths.dataIntegrations.history)}
          >
            Import History
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadHealth}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(paths.dataIntegrations.wizard)}
          >
            New Connection
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Hidden file input shared across all Manual rows for re-upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        hidden
        onChange={handleFileChosen}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : integrations.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No data integrations configured yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Connect your CRM or backend data source to start importing leads, appointments, and sales.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(paths.dataIntegrations.wizard)}
            >
              Set Up Your First Connection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {integrations.map((item) => {
            const key = rowKey(item);
            const busy = !!rowBusy[key];
            const isManual = item.integration_type === 'manual';
            const isPaused = item.enabled === false;
            return (
              <Card
                key={key}
                variant="outlined"
                sx={{
                  opacity: isPaused && !isManual ? 0.7 : 1,
                  transition: 'opacity 150ms',
                }}
              >
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.client_name || 'Unknown Client'}
                        </Typography>
                        <Chip
                          label={TYPE_LABELS[item.integration_type] || item.integration_type}
                          size="small"
                          variant="outlined"
                        />
                        {isPaused && !isManual && (
                          <Chip label="Paused" size="small" color="warning" variant="filled" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {isManual
                          ? (item.last_run_at
                            ? `Last upload: ${timeSince(item.last_run_at)}`
                            : 'No files uploaded yet')
                          : (
                            <>
                              Schedule: {item.schedule_type || 'N/A'}
                              {item.run_time ? ` at ${item.run_time}` : ''}
                            </>
                          )}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center">
                      {!isManual && (
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Last import
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {timeSince(item.last_run_at)}
                          </Typography>
                        </Box>
                      )}
                      <Chip
                        label={item.last_run_status || 'No runs'}
                        color={STATUS_COLORS[item.last_run_status] || 'default'}
                        size="small"
                      />

                      {/* Per-row actions */}
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {!isManual && (
                          <Tooltip title={isPaused ? 'Resume scheduled imports' : 'Pause scheduled imports'}>
                            <span>
                              <Switch
                                size="small"
                                checked={!isPaused}
                                disabled={busy}
                                onChange={(e) => handleToggleEnabled(item, e.target.checked)}
                              />
                            </span>
                          </Tooltip>
                        )}
                        {isManual && (
                          <Tooltip title="Upload a new file using this mapping">
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={busy ? <CircularProgress size={14} /> : <UploadFileIcon />}
                                onClick={() => handleUploadClick(item)}
                                disabled={busy}
                              >
                                Upload file
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Remap columns / transform rules">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setRemapping(item)}
                              disabled={busy}
                            >
                              <TuneIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!isManual && (
                          <Tooltip title="Edit schedule / credentials">
                            <span>
                              <IconButton size="small" onClick={() => setEditing(item)} disabled={busy}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete this integration">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete(item)}
                              disabled={busy}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Edit dialog */}
      <DataIntegrationEditDialog
        open={!!editing}
        mapping={editing}
        onClose={() => setEditing(null)}
        onSaved={() => loadHealth()}
      />

      {/* Remap columns dialog */}
      <DataIntegrationRemapDialog
        open={!!remapping}
        mapping={remapping}
        initialSample={driftSample}
        columnDrift={driftInfo}
        onClose={() => {
          setRemapping(null);
          setDriftSample(null);
          setDriftInfo(null);
        }}
        onSaved={() => {
          loadHealth();
          // Skip the generic toast when we're in the drift flow — the
          // onSavedWithSample handler below surfaces its own toast after
          // the auto-retry, which is more informative.
          if (!driftSample) {
            message.success('Column mapping updated');
          }
        }}
        onSavedWithSample={async (file) => {
          // The saved mapping is now correct for this file, so re-run the
          // upload with force=true to bypass the column-drift guard (the
          // guard checks against the *just-saved* mapping, which could
          // still be out of sync if the user manually added headers in
          // the dialog that aren't in the file — belt and suspenders).
          const item = remapping;
          setDriftSample(null);
          setDriftInfo(null);
          if (item && file) {
            message.success('Mapping updated — retrying your upload…');
            await runManualUpload(item, file, { force: true });
          }
        }}
      />

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove this integration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDelete?.client_name} — {TYPE_LABELS[confirmDelete?.integration_type] || confirmDelete?.integration_type}.
            The mapping and its schedule will be deleted. Already-imported data is kept.
            This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmDelete && handleDelete(confirmDelete)}
          >
            Delete integration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toasts are surfaced via the app-wide notistack provider (useMessage). */}
    </Box>
  );
}
